import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryFetch } from 'sonata-react-components';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import { preprocessCsvFile } from '@/services/file.service';
import { VENUE_COLUMNS } from '@/utils/files/constants';
import { CsvImporter } from '../CsvImporter/CsvImporter';
import { buildInitialMapping, DELIMITER_CHARS } from '../CsvImporter/csvImporter.helpers';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type {
  CsvImporterColumn,
  CsvImporterColumnGroup,
  CsvImporterConfig,
  CsvMapping
} from '../CsvImporter/csvImporter.helpers';
import type { FileData } from './POIImporter.hooks';

function getVenueColumns(usePoiFinder: boolean) {
  const overrides: Record<string, boolean> = usePoiFinder
    ? {
        country: false,
        point_type: true
      }
    : {
        latitude: true,
        longitude: true,
        point_type: false
      };

  return VENUE_COLUMNS.filter(column => (usePoiFinder ? column.key !== 'country' : column.key !== 'point_type')).map(
    c =>
      c.key in overrides
        ? {
            ...c,
            require: overrides[c.key]
          }
        : c
  );
}

const CAMPAIGN_METADATA_COLUMN_KEYS = ['venueName', 'venueGroup'];

type Props = {
  entity: {
    id: string;
    type: MultiFlightEntityType;
  };
  enablePoiFinder: boolean;
  file: File;
  onApply: (fileData: FileData) => Promise<boolean>;
  onFileChange: (file: File) => void;
  onGoBack: () => void;
  onFinish: () => void;
};

type State = {
  country?: string;
  config: CsvImporterConfig;
  mapping: CsvMapping[];
  processing: boolean;
};

export function POIImporter({ file, enablePoiFinder, onGoBack, onApply, onFileChange }: Props) {
  const { formatMessage } = useIntl();
  const [{ processing, ...form }, setState] = useState<State>({
    config: {
      delimiter: 'semicolon',
      separator: 'comma'
    },
    mapping: [],
    processing: false
  });
  const previousHeadersRef = useRef<string[]>([]);

  const countries = worldCountries.map(c => ({
    id: c.cca3,
    label: c.name.common
  }));
  const currentCountry = countries.filter(c => c.id === form.country);

  const tooltipTextMap = {
    address: formatMessage({
      defaultMessage:
        '"Address" refers to specific designations such as street, avenue, road, boulevard, drive, court, square, etc.',
      id: 'poiImporter.columnSelector.address.tooltip'
    }),
    latitude: formatMessage({
      defaultMessage:
        'Do not swap Latitude and Longitude, and make sure to maintain the decimal separator you configured above.',
      id: 'poiImporter.columnSelector.latitude.tooltip'
    }),
    longitude: formatMessage({
      defaultMessage:
        'Do not swap Latitude and Longitude, and make sure to maintain the decimal separator you configured above.',
      id: 'poiImporter.columnSelector.longitude.tooltip'
    }),
    point_type: formatMessage({
      defaultMessage:
        '"Brands" refer to specific entities such as McDonald\'s, Starbucks, etc., while "Categories" encompass broader industries like restaurants, pharmacies, hotels, and others.',
      id: 'poiImporter.columnSelector.brands.tooltip'
    }),
    venueGroup: formatMessage({
      defaultMessage: `A group is a way to organize related POIs so they can be easily identified and managed together. For example, you can group POIs by brand (e.g., McDonald's or Starbucks) to make filtering and searching simpler.`,
      id: 'poiImporter.columnSelector.group.tooltip'
    }),
    venueName: formatMessage({
      defaultMessage:
        '"Name" is what distinguishes one POI from another in your file. You may use a numeric ID or descriptive text.',
      id: 'poiImporter.columnSelector.name.tooltip'
    })
  };

  const columnOptions: CsvImporterColumn[] = getVenueColumns(enablePoiFinder).map(col => ({
    key: col.key,
    label: formatMessage({ defaultMessage: col.key, id: col.cpy }),
    regexp: col.regexp as RegExp,
    required: col.require,
    ...(col.key === 'address' && !enablePoiFinder ? { fullWidth: true } : {}),
    ...(tooltipTextMap[col.key as keyof typeof tooltipTextMap]
      ? { tooltipText: tooltipTextMap[col.key as keyof typeof tooltipTextMap] }
      : {})
  }));

  const columnGroups: CsvImporterColumnGroup[] = [
    {
      columns: columnOptions.filter(column => !CAMPAIGN_METADATA_COLUMN_KEYS.includes(column.key)),
      description: formatMessage({
        defaultMessage: 'we use this data to accurately locate your POIs on Google Maps.',
        id: 'poiImporter.config.geospatial.description'
      }),
      key: 'geospatial',
      label: formatMessage({
        defaultMessage: 'Geospatial information: ',
        id: 'poiImporter.config.geospatial.label'
      }),
      notice: {
        secondaryText: formatMessage({
          defaultMessage: 'For the best match accuracy, provide both (addresses alone can be imprecise).',
          id: 'poiImporter.status.info.detail'
        }),
        text: formatMessage({
          defaultMessage: 'You must provide coordinates and/or an address.',
          id: 'poiImporter.status.info.headline'
        })
      }
    },
    {
      columns: columnOptions.filter(column => CAMPAIGN_METADATA_COLUMN_KEYS.includes(column.key)),
      description: formatMessage({
        defaultMessage: 'customize these fields to organize your POIs on the platform.',
        id: 'poiImporter.config.campaignMetadata.description'
      }),
      key: 'campaignMetadata',
      label: formatMessage({
        defaultMessage: 'Campaign metadata (Sonata): ',
        id: 'poiImporter.config.campaignMetadata.label'
      })
    }
  ];

  const { data: preprocess, isFetching: isPreprocessFetching } = useQueryFetch(
    [
      'my_venues_poi_importer',
      form.country,
      form.config.delimiter,
      form.config.separator,
      file?.name,
      file?.lastModified,
      form.mapping.filter(entry => ['address', 'latitude', 'longitude'].includes(entry.key))
    ],
    async () => {
      const result = await preprocessCsvFile({
        columnMapping: form.mapping.filter(entry => entry.fileCol && entry.fileCol.length > 0),
        delimiter: form.config.delimiter,
        file: file!,
        numberOfLines: 50,
        numericSeparator: form.config.separator,
        rowLimitKey: 'poiFinder'
      });

      if (result?.headers && result.status === 'success') {
        const headersChanged =
          result.headers.length !== previousHeadersRef.current.length ||
          result.headers.some((header, index) => header !== previousHeadersRef.current[index]);
        previousHeadersRef.current = result.headers;

        if (headersChanged) {
          setState(prevState => ({
            ...prevState,
            mapping: buildInitialMapping(columnOptions, result.headers, prevState.mapping)
          }));
        }
      }

      return result;
    },
    {
      cacheTime: 0,
      enabled: !!file,
      staleTime: 0
    }
  );

  const requiredKeys = useMemo(
    () =>
      getVenueColumns(enablePoiFinder)
        .filter(col => col.require)
        .map(col => col.key),
    [enablePoiFinder]
  );

  const hasLat = form.mapping.some(entry => entry.key === 'latitude');
  const hasLng = form.mapping.some(entry => entry.key === 'longitude');
  const hasAddress = form.mapping.some(entry => entry.key === 'address');
  const hasLocationColumns = (hasLat && hasLng) || hasAddress;

  const isAvailableToSend =
    preprocess?.status === 'success' &&
    (!enablePoiFinder || !!form.country) &&
    hasLocationColumns &&
    requiredKeys.every(reqKey => form.mapping.some(entry => entry.key === reqKey));

  function handleCountryChange({ id: optionId }: SelectorOptionPropsInterface) {
    const country = optionId.toString();
    setState(prevState => ({
      ...prevState,
      country
    }));
  }

  function handleConfigChange(config: CsvImporterConfig) {
    setState(prevState => ({
      ...prevState,
      config,
      mapping: config.delimiter === prevState.config.delimiter ? prevState.mapping : []
    }));
  }

  async function handleApplyClick() {
    setState(prevState => ({ ...prevState, processing: true }));
    const success = await onApply({
      columnDelimiter: DELIMITER_CHARS[form.config.delimiter],
      country: form.country,
      decimalDelimiter: DELIMITER_CHARS[form.config.separator],
      exceedsMaxRows: preprocess?.exceedsMaxRows,
      fileName: preprocess!.s3Path!.replace('s3://sonataplatform.com/', ''),
      keysToReview: form.mapping,
      lineDelimiter: '',
      totalLines: 100
    });
    if (!success) {
      setState(prevState => ({ ...prevState, processing: false }));
    }
  }

  return (
    <CsvImporter
      applyText={
        preprocess?.exceedsMaxRows
          ? formatMessage({ defaultMessage: 'Send to support', id: 'poiImporter.sendToSupport' })
          : formatMessage({ defaultMessage: 'Apply', id: 'poiImporter.apply' })
      }
      columnGroups={columnGroups}
      config={form.config}
      {...(enablePoiFinder
        ? {
            country: {
              onChange: handleCountryChange,
              options: countries,
              value: currentCountry
            }
          }
        : {})}
      file={file}
      idPrefix="poiImporter"
      isApplyEnabled={isAvailableToSend}
      isApplying={processing}
      isPreprocessing={isPreprocessFetching}
      mapping={form.mapping}
      {...(preprocess?.exceedsMaxRows
        ? {
            notice: {
              secondaryText: formatMessage({
                defaultMessage:
                  'Click "Send to support," and someone from our team will manage your POI upload and reach out to you.',
                id: 'poiImporter.status.info.exceedsMaxRows.detail'
              }),
              text: formatMessage({
                defaultMessage: 'Large file detected.',
                id: 'poiImporter.status.info.exceedsMaxRows.headline'
              }),
              type: 'info' as const
            }
          }
        : {})}
      onApply={handleApplyClick}
      onConfigChange={handleConfigChange}
      onFileChange={onFileChange}
      onGoBack={onGoBack}
      onMappingChange={mapping => setState(prevState => ({ ...prevState, mapping }))}
      preprocess={preprocess}
      subtitle={formatMessage({
        defaultMessage: 'Map your columns to ensure your Points of Interest (POIs) upload correctly.',
        id: 'poiImporter.config.subtitle'
      })}
      title={formatMessage({
        defaultMessage: 'File configuration & column mapping',
        id: 'poiImporter.config.title'
      })}
    />
  );
}
